import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal/Modal';
import Typography from '../Typography';
import Button from '../Button/Button';
import axiosClient from '../../api/axiosClient';
import SpinnerCircular from '../SpinnerCircular';

const resolveStatusCopy = (t, checkout, fallbackContext = {}) => {
  const organizerName = checkout?.support?.organizerName || fallbackContext.organizerName || ''
  const eventTitle = checkout?.support?.eventTitle || fallbackContext.eventTitle || ''
  const chargeDisplayName = checkout?.chargeDisplayName || organizerName || eventTitle || 'Motorik'

  const statusMap = {
    pending_payment: {
      title: t('paymentStatus.pending.title'),
      description: t('paymentStatus.pending.description'),
    },
    processing: {
      title: t('paymentStatus.processing.title'),
      description: t('paymentStatus.processing.description'),
    },
    processing_expired: {
      title: t('paymentStatus.processingExpired.title'),
      description: t('paymentStatus.processingExpired.description'),
    },
    requires_action: {
      title: t('paymentStatus.requiresAction.title'),
      description: t('paymentStatus.requiresAction.description'),
    },
    requires_payment_method: {
      title: t('paymentStatus.requiresPaymentMethod.title'),
      description: t('paymentStatus.requiresPaymentMethod.description'),
    },
    enrollment_pending: {
      title: t('paymentStatus.enrollmentPending.title'),
      description: t('paymentStatus.enrollmentPending.description'),
    },
    confirmed: {
      title: t('paymentStatus.confirmed.title'),
      description: t('paymentStatus.confirmed.description'),
    },
    cancelled: {
      title: t('paymentStatus.cancelled.title'),
      description: t('paymentStatus.cancelled.description'),
    },
    failed: {
      title: t('paymentStatus.failed.title'),
      description: t('paymentStatus.failed.description'),
    },
    late_payment_review: {
      title: t('paymentStatus.latePaymentReview.title'),
      description: t('paymentStatus.latePaymentReview.description'),
    },
    refund_required: {
      title: t('paymentStatus.refundRequired.title'),
      description: t('paymentStatus.refundRequired.description'),
    },
    payment_intent_reconciliation_required: {
      title: t('paymentStatus.reconciliationRequired.title'),
      description: t('paymentStatus.reconciliationRequired.description'),
    },
  }

  return {
    ...(statusMap[checkout?.uiStatus] || statusMap.pending_payment),
    chargeDisplayName,
    organizerName,
    eventTitle,
  }
}

const PaidCheckoutStatusModal = ({
  isOpen,
  checkoutContext,
  event,
  onClose,
  onClearCheckout,
  onRetryPayment,
  onViewEnrollment,
  onCheckoutConfirmed,
}) => {
  const { t } = useTranslation('eventEnroll');
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const confirmedCheckoutRef = useRef(null);

  const fetchCheckoutStatus = useCallback(async ({ silent = false } = {}) => {
    if (!checkoutContext?.paymentId || !checkoutContext?.reservationId) {
      return null;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await axiosClient.get('/api/stripe/checkout-status', {
        params: {
          paymentId: checkoutContext.paymentId,
          reservationId: checkoutContext.reservationId,
          paymentIntentId: checkoutContext.paymentIntentId || undefined,
        }
      });

      setCheckout(response.data.checkout);
      setErrorMessage('');
      return response.data.checkout;
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
        t('paymentStatus.errors.fetchStatus')
      );
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [checkoutContext, t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    confirmedCheckoutRef.current = null;
    fetchCheckoutStatus();
  }, [fetchCheckoutStatus, isOpen]);

  useEffect(() => {
    if (!isOpen || !checkout?.pollingRecommended) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchCheckoutStatus({ silent: true });
    }, 4000);

    return () => clearInterval(intervalId);
  }, [checkout?.pollingRecommended, fetchCheckoutStatus, isOpen]);

  useEffect(() => {
    if (!checkout?.attendeeConfirmed) {
      return;
    }

    if (confirmedCheckoutRef.current === checkout.paymentId) {
      return;
    }

    confirmedCheckoutRef.current = checkout.paymentId;
    onCheckoutConfirmed?.(checkout);
  }, [checkout, onCheckoutConfirmed]);

  const copy = useMemo(() => resolveStatusCopy(t, checkout, {
    organizerName: event?.owner?.name,
    eventTitle: event?.title,
  }), [checkout, event?.owner?.name, event?.title, t]);

  const shouldShowRetry = Boolean(checkout?.retryAllowed);
  const shouldShowRefresh = Boolean(checkout && !checkout.attendeeConfirmed);
  const retryLabel = checkout?.uiStatus === 'pending_payment'
    ? t('paymentStatus.actions.continuePayment')
    : t('paymentStatus.actions.retryPayment');

  return (
    <Modal
      title={t('paymentStatus.title')}
      onClose={onClose}
      maxWidth="540px"
    >
      <Container>
        {loading ? (
          <StatusCard>
            <LoadingRow>
              <SpinnerCircular size={20} color="#10110f" secondaryColor="rgba(0,0,0,0.12)" />
              <Typography $variant="title-5-semibold">
                {t('paymentStatus.loading')}
              </Typography>
            </LoadingRow>
            <Typography $variant="body-1-regular">
              {t('paymentStatus.loadingDescription')}
            </Typography>
          </StatusCard>
        ) : (
          <>
            <StatusCard>
              <Typography $variant="title-5-semibold">
                {copy.title}
              </Typography>
              <Typography $variant="body-1-regular">
                {copy.description}
              </Typography>
            </StatusCard>

            <DetailGrid>
              <DetailCard>
                <Typography $variant="caption-medium" color="#6b7280">
                  {t('paymentStatus.chargeLabel')}
                </Typography>
                <Typography $variant="body-1-semibold">
                  {copy.chargeDisplayName}
                </Typography>
              </DetailCard>

              {copy.organizerName && (
                <DetailCard>
                  <Typography $variant="caption-medium" color="#6b7280">
                    {t('paymentStatus.organizerLabel')}
                  </Typography>
                  <Typography $variant="body-1-semibold">
                    {copy.organizerName}
                  </Typography>
                </DetailCard>
              )}
            </DetailGrid>

            {(checkout?.support?.canContactOrganizer || event?.owner?.name) && (
              <SupportCard>
                <Typography $variant="body-2-semibold">
                  {t('paymentStatus.supportTitle')}
                </Typography>
                <Typography $variant="body-2-regular">
                  {t('paymentStatus.supportDescription')}
                </Typography>
              </SupportCard>
            )}

            {errorMessage && (
              <ErrorText>{errorMessage}</ErrorText>
            )}

            <Actions>
              {checkout?.attendeeConfirmed ? (
                <>
                  <Button
                    size="l"
                    onClick={() => {
                      onClearCheckout?.();
                      onViewEnrollment?.();
                    }}
                  >
                    {t('paymentStatus.actions.viewEnrollment')}
                  </Button>
                  <Button
                    $variant="outline"
                    size="l"
                    onClick={onClearCheckout}
                  >
                    {t('paymentStatus.actions.close')}
                  </Button>
                </>
              ) : (
                <>
                  {shouldShowRetry && (
                    <Button
                      size="l"
                      onClick={() => onRetryPayment?.(checkout)}
                    >
                      {retryLabel}
                    </Button>
                  )}
                  {shouldShowRefresh && (
                    <Button
                      $variant={shouldShowRetry ? 'outline' : 'default'}
                      size="l"
                      onClick={() => fetchCheckoutStatus()}
                    >
                      {t('paymentStatus.actions.refreshStatus')}
                    </Button>
                  )}
                  <Button
                    $variant={shouldShowRetry || shouldShowRefresh ? 'ghost' : 'outline'}
                    size="l"
                    onClick={
                      checkout?.uiStatus === 'late_payment_review' ||
                      checkout?.uiStatus === 'refund_required' ||
                      checkout?.uiStatus === 'cancelled' ||
                      checkout?.uiStatus === 'failed'
                        ? onClearCheckout
                        : onClose
                    }
                  >
                    {t('paymentStatus.actions.close')}
                  </Button>
                </>
              )}
            </Actions>
          </>
        )}
      </Container>
    </Modal>
  );
};

PaidCheckoutStatusModal.propTypes = {
  isOpen: PropTypes.bool,
  checkoutContext: PropTypes.shape({
    paymentId: PropTypes.string,
    reservationId: PropTypes.string,
    paymentIntentId: PropTypes.string,
  }),
  event: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onClearCheckout: PropTypes.func,
  onRetryPayment: PropTypes.func,
  onViewEnrollment: PropTypes.func,
  onCheckoutConfirmed: PropTypes.func,
};

export default PaidCheckoutStatusModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const StatusCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.fill.brandSubtle || '#f3f4f6'};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle || '#e5e7eb'};
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 12px;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle || '#e5e7eb'};
`;

const SupportCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  border-radius: 12px;
  background: #fff8e7;
  border: 1px solid #f5d98f;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #b42318;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
